// TODO: This file should be broken out into a separate Wilson-specific style guide-related folder.

// ******************************************************************************************************************
//
// This contains all the functions necessary to work with "WilsonModal.cshtml"
//
// Parameters - htmlContent, { actionRequired, dialogSize, modalTitle, buttonBehavior, primaryFunction, optionalFunction, modalClass }
//
//        * actionRequired (boolean): If true, the user can't dismiss the modal just by clicking outside
//                                    of it, there will be a dark overlay on the page, and scrolling behind
//                                    the modal will be disabled .If false, the user can click outside the
//                                    modal to dismiss it, there won't be any overlay on the page, and they
//                                    will still be able to scroll.
//        * dialogSize (string): Values available are 'xl,' 'lg,' 'md,' and 'sm.' This determines the size
//                               of the modal. 'xl' will take up almost the whole page, 'lg' is the standard
//                               size, and 'md' and 'sm' are steps down from the standard size.
//
//        * modalTitle (string): This will determine the content of the top title bar of the modal. It will
//                               accept HTML content if need be (maybe a <sup> or something).
//
//        * htmlContent: In some cases, this will be a string value, but in probably most cases, it will
//                       actually pass in partial view!
//
//        * buttonBehavior (function name): Here, you will pass in the buttons you need via their function name.
//                                     For example, if you need a save and cancel button, the value of this
//                                     parameter should be setSaveCancel. Here is a list of available button
//                                     configuration functions:
//                                         - setOkay
//                                         - setContinueCancel
//                                         - setDeleteSaveCancel
//                                         - setSaveCancel
//                                         - setSendCancel
//                                         - setSubmitCancel
//                                         - setClose
//                                         - setCancel
//
//        *primaryFunction (function name): Here you will pass in the name of the function that the primary
//                                          button will call when clicked. Primary buttons are 'Continue,'
//                                          'Save,' 'Send,' and 'Submit.' If no function is to be called for
//                                          any of these, set to false, but this probably will never happen.
//
//        *optionalFunction (function name): Here you will pass in the name of the function that the secondary
//                                           button will call when clicked if applicable. Most of the time this
//                                           will probably be set to false. This paremeter is used on the
//                                           'Delete' button and the 'Cancel' button configured in
//                                           setContinueCancel();. Even though it may be unused in most cases,
//                                           all button configurations call it if not false, so you could use
//                                           this for some other behind the scenes behavior!
//        *modalClass (string): Class to get attached to modal container. Styling/scoping purposes.
//
// ******************************************************************************************************************
export enum DialogSize {
    ExtraLarge = "xl",
    Large = "lg",
    Medium = "md",
    Small = "sm"
}

interface ModalOptions{
    actionRequired?;
    dialogSize?: DialogSize;
    modalTitle?: string;
    buttonBehavior?: Function;
    primaryFunction?: Function | boolean;
    optionalFunction?: Function | boolean;
    modalClass? : string
}

// Flags
var modalActive = false; // will see timing issues if trying to pop a modal while another is closing without this flag.

let refreshOnClose = false;

//Modal Button constants
export const MODAL_OKAY = $('#okay-btn');
export const MODAL_CONTINUE = $('#continue-btn');
export const MODAL_CANCEL = $('#cancel-btn');
export const MODAL_DELETE = $('#delete-btn');
export const MODAL_SAVE = $('#save-btn');
export const MODAL_SEND = $('#send-btn');
export const MODAL_SUBMIT = $('#submit-btn');
export const MODAL_CLOSE = $('#close-btn');
// Popping a Modal!
// *******************************************************************************
export function triggerModal(html, overrideOptions: ModalOptions) {
    const defaultOptions: ModalOptions = {
        actionRequired: true,
        dialogSize: DialogSize.Medium,
        modalTitle: 'Wilson Academy',
        buttonBehavior: setContinueCancel,
        primaryFunction: false,
        optionalFunction: false,
        modalClass: null
    }
    const options = { ...defaultOptions, ...(overrideOptions || {}) }
    // Make sure there isn't already an active modal...
    if (!modalActive) {
        configureModal(options.actionRequired, options.dialogSize, options.modalClass);
        setModalContents(options.modalTitle, html, options.buttonBehavior, options.primaryFunction, options.optionalFunction);
        showModal();
    }
    // If there is an active modal, wait a second and try again...
    else {
        retryModal(html, options);
    }

}

function configureModal(actionRequired, dialogSize, modalClass) {
    const modalDialog = $('#modal-dialog');
    // set the size of the modal
    switch (dialogSize)
    {
        case ('xl'):
            modalDialog.addClass('xl'); // this will take up almost literally the entire screen.
            break;
        case ('lg'):
            modalDialog.addClass('lg'); // this is the standard-size modal.
            break;
        case ('md'):
            modalDialog.addClass('md'); // this is just a tad smaller than the standard size.
            break;
        case ('sm'):
            modalDialog.addClass('sm'); // this is the smallest modal (for now).
            break;
        default:
            // do literally nothing.
    }

    if (modalClass) {
        modalDialog.addClass(modalClass);
    }

    // If 'actionRequired' param is true...
    if (actionRequired === true) {
        // ...prevent scrolling behind modal
        $('body').addClass('modal-open');
        // ...don't allow clicking out of the modal to dismiss the modal
        $('#wilson-modal').attr("data-dismiss", "");
    }

}

function setModalContents(modalTitle, htmlContent, buttonBehavior, primaryFunction, optionalFunction) {
    $('#modal-title').html(modalTitle); // accepts HTML in case we need to accept a <sup> or something.
    $('#modal-body').html(htmlContent);
    // Call the function to configure the footer and pass the necessary functions
    buttonBehavior(primaryFunction, optionalFunction);
}


// Setup Modal Footers
// *******************************************************************************
export function setOkay(primaryFunction, optionalFunction) {
    MODAL_OKAY.show();
    if (primaryFunction) {
        MODAL_OKAY.on('click', function () {
            primaryFunction();
        });
    }

    if (optionalFunction) {
        optionalFunction();
    }
}

export function setContinueCancel(primaryFunction, optionalFunction) {
   MODAL_CONTINUE.show();
    if (primaryFunction) {
       MODAL_CONTINUE.on('click', function () {
            primaryFunction();
        });
    } else {
       MODAL_CONTINUE.on('click', function () {
            closeModal();
        });
    }

    MODAL_CANCEL.show();
    if (optionalFunction) {
        MODAL_CANCEL.on('click', function () {
            optionalFunction();
        });
    } else {
        MODAL_CANCEL.on('click', function () {
            closeModal();
        });
    }
}

export function setDeleteSaveCancel(primaryFunction, optionalFunction) {
    MODAL_DELETE.show().on('click', function () {
        optionalFunction();
    });
    MODAL_SAVE.show().on('click', function () {
        primaryFunction();
    });
    MODAL_CANCEL.show();

    if (optionalFunction) {
        optionalFunction();
    }
}

export function setSaveCancel(primaryFunction, optionalFunction) {
    MODAL_SAVE.show().on('click', function () {
        primaryFunction();
    });
    MODAL_CANCEL.show();

    if (optionalFunction) {
        optionalFunction();
    }
}

export function setSendCancel(primaryFunction, optionalFunction) {
    MODAL_SEND.show().show().on('click', function () {
        primaryFunction();
    });
    MODAL_CANCEL.show();

    if (optionalFunction) {
        optionalFunction();
    }
}

export function setSaveClose(primaryFunction, optionalFunction) {
    MODAL_SAVE.show().show().on('click', function () {
        primaryFunction();
    });
    MODAL_CLOSE.show();

    if (optionalFunction) {
        optionalFunction();
    }
}

export function setSubmitCancel(primaryFunction, optionalFunction) {
    MODAL_SUBMIT.show().show().on('click', function () {
        primaryFunction();
    });
    MODAL_CANCEL.show();

    if (optionalFunction) {
        optionalFunction();
    }
}

export function setClose(primaryFunction, optionalFunction) {
    MODAL_CLOSE.show()

    if (primaryFunction) {
        primaryFunction();
    }
    if (optionalFunction) {
        optionalFunction();
    }
}

export function setCancel(primaryFunction, optionalFunction) {
    MODAL_CANCEL.show();
    if (primaryFunction) {
        MODAL_CANCEL.on('click', function () {
            primaryFunction();
        });
    }

    if (optionalFunction) {
        optionalFunction();
    }
}


// If there is already a modal open, this will wait 2 seconds and try again.
// It will only retry one time before it pops an alert box with an error message.
var retryCounter = 0;
function retryModal(html, options) {
    var pauseBeforeRetry = function () {
        if (retryCounter < 2) {
            triggerModal(html, options);
        } else {
            alert('Oops! It looks like there was a problem with that request. Refresh the page and try again!');
        }
    }
    window.setTimeout(pauseBeforeRetry, 2000);
}


// TODO: Replace with something meaningful
function errorModal() {
    alert('Oops! There was an error.');
}


// Open modal and fire custom 'open-modal' event
function showModal() {
    $('#wilson-modal').show().trigger('open-modal').focus();
}


// Closing a Modal
// *******************************************************************************
// Close modal when element with data-dismiss="modal" is clicked.
// This includes the 'X' and the okay, close, and cancel buttons.
$('body').on('click', '[data-dismiss="modal"]', function () {
    closeModal();
});


// Close modal and fire custom 'close-modal' event
export function closeModal() {
    $('#wilson-modal').hide().trigger('close-modal');
    $('body').removeClass('modal-open');
    $('#modal-dialog').removeClass();
}


// Clear out modal content and hide button groupings
function resetModal() {
    $('#modal-title').html('');
    $('#modal-body').html('');
    MODAL_DELETE.hide();
    MODAL_OKAY.hide();
    MODAL_CONTINUE.hide();
    MODAL_SAVE.hide();
    MODAL_SEND.hide();
    MODAL_SUBMIT.hide();
    MODAL_CLOSE.hide();
    MODAL_CANCEL.hide();

    //Unbind all events for all buttons.
    MODAL_SAVE.off('click');
    MODAL_SUBMIT.off('click');
    MODAL_SEND.off('click');
    MODAL_CONTINUE.off('click');
    MODAL_DELETE.off('click');

    MODAL_OKAY.off('click');
    MODAL_CLOSE.off('click');
    MODAL_CANCEL.off('click');
}

export function refreshModalOnClose() {
    refreshOnClose = true;
}

export function changeModalButtonsToClose() {

    if (MODAL_SUBMIT.is(":visible")) {
        MODAL_SUBMIT.hide();
    }
    if (MODAL_SAVE.is(":visible")) {
        MODAL_SAVE.hide();
    }
    if (MODAL_SEND.is(":visible")) {
        MODAL_SEND.hide();
    }
    if (MODAL_OKAY.is(":visible")) {
        MODAL_OKAY.hide();
    }
    if ($('#continue-btn').is(":visible")) {
       MODAL_CONTINUE.hide();
    }
    if (MODAL_DELETE.is(":visible")) {
        MODAL_DELETE.hide();
    }
    if (MODAL_CANCEL.is(":visible")) {
        MODAL_CANCEL.hide();
    }
    if (MODAL_CLOSE.is(":hidden")) {
        MODAL_CLOSE.show();
    }
    $('#wilson-modal').focus();
} 

// Listen for Custom Events (& Do Things)
// *******************************************************************************
$(function () {

    let lastFocusedElement;

    // save last focused element that triggered modal
    $('.modal-trigger').on('click', function (e) {
        lastFocusedElement = this;
    });

    // Listen for when modal is opened
    $('#wilson-modal').on('open-modal', function () {

        // Unhide the modal from screen readers
        $(this).attr('aria-hidden', 'false');

        // Set the "Active" flag
        modalActive = true;
    });

    // Listen for when modal is closed
    $('#wilson-modal').on('close-modal', function () {
        // Clear modal content once it's closed
        resetModal(); 
        // Hide the modal from screen readers
        $(this).attr('aria-hidden', 'true');

        // Reset the "Active" flag
        modalActive = false;

        // Restore focus to the link that opened the modal
        $(lastFocusedElement).focus(); 

        if (refreshOnClose) {
            location.reload();
        }
    });

    $('#wilson-modal').modalTabbing();
});


$.fn.modalTabbing = function () {

    let tabbing = function (jqSelector) {
        let inputs = $(jqSelector).find('select, input, textarea, button, a[href]').filter(':visible').not(':disabled');

        //Focus to first element in the container.
        inputs.first().focus();

        $(jqSelector).on('keydown', function (e) {
            if (e.which === 9) {

                let inputs = $(jqSelector).find('select, input, textarea, button, a[href]').filter(':visible').not(':disabled');

                /*redirect last tab to first input*/
                if (!e.shiftKey) {
                    if (inputs[inputs.length - 1] === e.target) {
                        e.preventDefault();
                        inputs.first().focus();
                    }
                }
                /*redirect first shift+tab to last input*/
                else if (inputs[0] === e.target) {
                    e.preventDefault();
                    inputs.last().focus();
                }
                
            }
        });
    };

    return this.each(function () {
        tabbing(this);
    });

};